
"use client";

import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import Color from "@tiptap/extension-color";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import TextStyle from "@tiptap/extension-text-style";
import { useEffect, useState } from "react";
import { 
  Bold, Italic, Underline as UnderlineIcon, AlignLeft, AlignCenter, 
  AlignRight, Heading1, Heading2, List, ListOrdered, Link as LinkIcon, 
  Image as ImageIcon, Undo, Redo, PaintBucket, Minus, Type, Quote, Code
} from "lucide-react";

export default function RichTextEditor({ value, onChange }) {
  const [mounted, setMounted] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3, 4, 5, 6] },
        bulletList: {
          keepMarks: true,
          keepAttributes: false,
        },
        orderedList: {
          keepMarks: true,
          keepAttributes: false,
        },
      }),
      Underline,
      TextStyle,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-cyan-600 underline',
        },
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'mx-auto my-4 max-w-full rounded-lg',
        },
        allowBase64: true,
      }),
      Color,
      TextAlign.configure({ 
        types: ["heading", "paragraph"],
        alignments: ['left', 'center', 'right'],
      }),
      Placeholder.configure({
        placeholder: 'Start writing your blog post...',
      }),
    ],
    content: value || "<p>Start typing...</p>",
    onUpdate: ({ editor }) => {
      onChange && onChange(editor.getHTML());
    },
    
  });

  // Handle image upload
  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(file.type)) {
      alert('Please upload only image files (jpeg, png, gif, webp)');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      if (!editor) return;
      // Insert image at current cursor position
      editor.chain().focus().setImage({ src: e.target.result }).run();
    };
    reader.readAsDataURL(file);
  };

  // Add image by URL
  const addImageByUrl = (e) => {
    e.preventDefault(); // Prevent form submission
    const url = prompt("Enter image URL");
    if (url && editor) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };

  // Set link
  const setLink = (e) => {
    e.preventDefault(); // Prevent form submission
    if (!editor) return;
    
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl);
    
    // cancelled
    if (url === null) return;
    
    // empty
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    
    // update link
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  if (!mounted || !editor) {
    return <div className="p-4 text-center">Loading editor...</div>;
  }

  return (
    <div className="border rounded-md overflow-hidden">
      {/* Toolbar */}
        <div className="flex flex-wrap gap-1 p-2 bg-gray-50 border-b">
            {/* Text formatting group */}
            <div className="flex space-x-1 mr-2">
            <button
                type="button" // Explicitly set button type
                onClick={(e) => {
                e.preventDefault(); // Prevent form submission
                editor.chain().focus().toggleBold().run();
                }}
                className={`p-2 rounded hover:bg-gray-200 ${
                editor.isActive("bold") ? "bg-gray-200" : ""
                }`}
                title="Bold"
            >
                <Bold className="w-5 h-5" />
            </button>
            
            <button
                type="button" // Explicitly set button type
                onClick={(e) => {
                e.preventDefault(); // Prevent form submission
                editor.chain().focus().toggleItalic().run();
                }}
                className={`p-2 rounded hover:bg-gray-200 ${
                editor.isActive("italic") ? "bg-gray-200" : ""
                }`}
                title="Italic"
            >
                <Italic className="w-5 h-5" />
            </button>
            
            <button
                type="button" // Explicitly set button type
                onClick={(e) => {
                e.preventDefault(); // Prevent form submission
                editor.chain().focus().toggleUnderline().run();
                }}
                className={`p-2 rounded hover:bg-gray-200 ${
                editor.isActive("underline") ? "bg-gray-200" : ""
                }`}
                title="Underline"
            >
                <UnderlineIcon className="w-5 h-5" />
            </button>
        </div>
        
        {/* Headings group */}
        <div className="flex space-x-1 mr-2">
            <button
                type="button" // Explicitly set button type
                onClick={(e) => {
                e.preventDefault(); // Prevent form submission
                editor.chain().focus().toggleHeading({ level: 1 }).run();
                }}
                className={`p-2 rounded hover:bg-gray-200 ${
                editor.isActive("heading", { level: 1 }) ? "bg-gray-200" : ""
                }`}
                title="Heading 1"
            >
                <Heading1 className="w-5 h-5" />
            </button>
            
            <button
                type="button" // Explicitly set button type
                onClick={(e) => {
                e.preventDefault(); // Prevent form submission
                editor.chain().focus().toggleHeading({ level: 2 }).run();
                }}
                className={`p-2 rounded hover:bg-gray-200 ${
                editor.isActive("heading", { level: 2 }) ? "bg-gray-200" : ""
                }`}
                title="Heading 2"
            >
                <Heading2 className="w-5 h-5" />
            </button>
          
            <button
                type="button" // Explicitly set button type
                onClick={(e) => {
                e.preventDefault(); // Prevent form submission
                editor.chain().focus().setParagraph().run();
                }}
                className={`p-2 rounded hover:bg-gray-200 ${
                editor.isActive("paragraph") ? "bg-gray-200" : ""
                }`}
                title="Paragraph"
            >
                <Type className="w-5 h-5" />
            </button>
        </div>
        
        {/* Lists group */}
        <div className="flex space-x-1 mr-2 ">
            <button
                type="button" // Explicitly set button type
                onClick={(e) => {
                e.preventDefault(); // Prevent form submission
                editor.chain().focus().toggleBulletList().run();
                }}
                className={`p-2 rounded hover:bg-gray-200 ${
                editor.isActive("bulletList") ? "bg-gray-200" : ""
                }`}
                title="Bullet List"
            >
                <List className="w-5 h-5" />
            </button>
            
            <button
                type="button" // Explicitly set button type
                onClick={(e) => {
                e.preventDefault(); // Prevent form submission
                editor.chain().focus().toggleOrderedList().run();
                }}
                className={`p-2 rounded hover:bg-gray-200 ${
                editor.isActive("orderedList") ? "bg-gray-200" : ""
                }`}
                title="Ordered List"
            >
                <ListOrdered className="w-5 h-5" />
            </button>
          
            <button
                type="button" // Explicitly set button type
                onClick={(e) => {
                e.preventDefault(); // Prevent form submission
                editor.chain().focus().toggleBlockquote().run();
                }}
                className={`p-2 rounded hover:bg-gray-200 ${
                editor.isActive("blockquote") ? "bg-gray-200" : ""
                }`}
                title="Quote"
            >
                <Quote className="w-5 h-5" />
            </button>
            
            <button
                type="button" // Explicitly set button type
                onClick={(e) => {
                e.preventDefault(); // Prevent form submission
                editor.chain().focus().toggleCodeBlock().run();
                }}
                className={`p-2 rounded hover:bg-gray-200 ${
                editor.isActive("codeBlock") ? "bg-gray-200" : ""
                }`}
                title="Code Block"
            >
                <Code className="w-5 h-5" />
            </button>
        </div>
        
        {/* Alignment group */}
        <div className="flex space-x-1 mr-2">
            <button
                type="button" // Explicitly set button type
                onClick={(e) => {
                e.preventDefault(); // Prevent form submission
                editor.chain().focus().setTextAlign('left').run();
                }}
                className={`p-2 rounded hover:bg-gray-200 ${
                editor.isActive({ textAlign: 'left' }) ? "bg-gray-200" : ""
                }`}
                title="Align Left"
            >
                <AlignLeft className="w-5 h-5" />
            </button>
            
            <button
                type="button" // Explicitly set button type
                onClick={(e) => {
                e.preventDefault(); // Prevent form submission
                editor.chain().focus().setTextAlign('center').run();
                }}
                className={`p-2 rounded hover:bg-gray-200 ${
                editor.isActive({ textAlign: 'center' }) ? "bg-gray-200" : ""
                }`}
                title="Align Center"
            >
                <AlignCenter className="w-5 h-5" />
            </button>
          
            <button
                type="button" // Explicitly set button type
                onClick={(e) => {
                e.preventDefault(); // Prevent form submission
                editor.chain().focus().setTextAlign('right').run();
                }}
                className={`p-2 rounded hover:bg-gray-200 ${
                editor.isActive({ textAlign: 'right' }) ? "bg-gray-200" : ""
                }`}
                title="Align Right"
            >
                <AlignRight className="w-5 h-5" />
            </button>
        </div>
        
        {/* Media and links group */}
        <div className="flex space-x-1 mr-2">
            <button 
                type="button" // Explicitly set button type
                onClick={setLink}
                className={`p-2 rounded hover:bg-gray-200 ${
                editor.isActive('link') ? "bg-gray-200" : ""
                }`}
                title="Add Link"
            >
                <LinkIcon className="w-5 h-5" />
            </button>
            
            <div className="relative">
                <button 
                type="button" // Explicitly set button type
                onClick={addImageByUrl}
                className="p-2 rounded hover:bg-gray-200"
                title="Add Image"
                >
                <ImageIcon className="w-5 h-5" />
                </button>
                
                {/* Hidden file input for image upload */}
                <input
                type="file"
                id="image-upload"
                accept="image/*"
                className="hidden"
                onChange={handleImageUpload}
                />
            
                {/* Image upload button that triggers file input */}
                <button 
                type="button" // Explicitly set button type
                onClick={(e) => {
                    e.preventDefault(); // Prevent form submission
                    document.getElementById('image-upload').click();
                }}
                className="p-2 rounded hover:bg-gray-200 ml-1"
                title="Upload Image"
                >
                <PaintBucket className="w-5 h-5" />
                </button>
          </div>
          
          <button 
            type="button" // Explicitly set button type
            onClick={(e) => {
              e.preventDefault(); // Prevent form submission
              editor.chain().focus().setHorizontalRule().run();
            }}
            className="p-2 rounded hover:bg-gray-200"
            title="Add Horizontal Line"
          >
            <Minus className="w-5 h-5" />
          </button>
        </div>
        
        {/* History group */}
        <div className="flex space-x-1 mr-2 ">
            <button 
                type="button" // Explicitly set button type
                onClick={(e) => {
                e.preventDefault(); // Prevent form submission
                editor.chain().focus().undo().run();
                }}
                disabled={!editor.can().undo()}
                className="p-2 rounded hover:bg-gray-200 disabled:opacity-50"
                title="Undo"
            >
                <Undo className="w-5 h-5" />
            </button>
            
            <button 
                type="button" // Explicitly set button type
                onClick={(e) => {
                e.preventDefault(); // Prevent form submission
                editor.chain().focus().redo().run();
                }}
                disabled={!editor.can().redo()}
                className="p-2 rounded hover:bg-gray-200 disabled:opacity-50"
                title="Redo"
            >
                <Redo className="w-5 h-5" />
            </button>
        </div>
      </div>
      
      {/* Editor Content */}
      <div className="p-4 min-h-96 bg-white ">
        <EditorContent editor={editor} className="prose max-w-none p-6 " />
      </div>
    </div>
  );
}